import React, { useRef } from 'react';
import { usePrintHook } from '../../hooks/usePrintHook';
import { decemberData } from '../../data/decemberData';
import MarketingReport from '../MarketingReport';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const StandaloneDecemberReport: React.FC = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { printRef } = usePrintHook();

  const generatePDF = async () => {
    if (!reportRef.current) return;

    try {
      const fileName = `ROI_Marketing_Report_December_2025.pdf`;

      const reportElement = reportRef.current;
      const canvas = await html2canvas(reportElement, {
        scale: 1.5,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;

      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;

      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }

      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }

      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleExportPDF = () => {
    generatePDF();
  };

  const handlePrint = () => {
    if (reportRef.current) {
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            December 2025 Marketing Performance Report
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Print
            </button>
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Export as PDF
            </button>
          </div>
        </div>

        <div ref={reportRef}>
          <MarketingReport data={decemberData} />
        </div>
      </div>
    </div>
  );
};

export default StandaloneDecemberReport;
